import React, { createContext, useContext, useState, useRef } from 'react';
import { Check, X as XIcon, Info, AlertTriangle } from 'lucide-react';

const ToastContext = createContext();

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = (id) => {
    // Cancel the auto-remove timer so it doesn't call removeToast a second time
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    // Mark as exiting for animation, then remove
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 250);
  };

  const addToast = (message, type = 'info', duration = 4000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type, exiting: false }]);
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    }
    return id;
  };

  const toastFns = {
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur || 6000),
    info: (msg, dur) => addToast(msg, 'info', dur),
    warning: (msg, dur) => addToast(msg, 'warning', dur),
  };

  // Lucide icons in a type-colored badge, more visual punch than the old
  // ASCII glyphs and consistent with the rest of the app's icon system.
  const renderIcon = (type) => {
    const sizes = { width: 14, height: 14, strokeWidth: 2.75 };
    const Icon = type === 'success' ? Check
      : type === 'error' ? XIcon
      : type === 'warning' ? AlertTriangle
      : Info;
    return <Icon {...sizes} aria-hidden="true" />;
  };

  return (
    <ToastContext.Provider value={toastFns}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}${t.exiting ? ' toast-exit' : ''}`}>
            <span className={`toast-icon-badge toast-icon-badge-${t.type}`} aria-hidden="true">
              {renderIcon(t.type)}
            </span>
            <span className="toast-content">{t.message}</span>
            <button className="toast-close" onClick={() => removeToast(t.id)} aria-label="Dismiss notification">
              <XIcon width={14} height={14} strokeWidth={2.25} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
